import { VentilationZones as VentilationZonesClient } from "@WahooFitness/cloud-client-ts";
import { VentilationZonesResponseType } from "@WahooFitness/cloud-client-types";
import { useCallback, useEffect, useMemo } from "react";
import { StoredZones, Zone, ZoneSet } from "./useZones";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { clearNulls } from "@/services/clearNulls";
import { t } from "@lingui/macro";
import {
  useCloudContext,
  useConfigContext,
  useUserContext,
  useNativeMessagingContext,
  useOfflineSWR,
} from "@WahooFitness/wahoo-offline-mfe";
import { copyZones } from "./ZonesUtils";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";

// DEX-788: ventilation zones have no cloud-provided primary/default set (unlike HR/power/pace),
// so a user creating their first set has nothing to clone. Seed evenly-spaced VE boundaries
// (L/min) ascending from the floor of 5; the user edits these before saving.
const DEFAULT_VENTILATION_ZONE_BOTTOMS = [5, 60, 120, 180, 240];

const useVentilationZones: () => StoredZones = () => {
  const { workoutProfiles, updateProfile, mutateWorkoutProfiles } = useWorkoutProfilesContext();

  const { wahooToken } = useConfigContext();
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();
  const { mutateUser } = useUserContext();
  const { getSettingsRemoteConfigObject } = useSettingsRemoteConfig();

  const { getCloudClient } = useCloudContext();
  const ventilationZonesClient = useMemo(
    () => getCloudClient(VentilationZonesClient),
    [getCloudClient]
  );

  const {
    data: ventilationZoneData,
    error,
    isLoading,
    mutate: mutateVentilationZones,
  } = useOfflineSWR(["getVentilationZones", wahooToken], async ([_key, wahooToken]) => {
    return clearNulls(await ventilationZonesClient.getForCurrentUser(wahooToken));
  });

  useEffect(() => {
    const refreshListener = addRefreshListener(mutateVentilationZones);
    return () => removeRefreshListener(refreshListener);
  }, [addRefreshListener, mutateVentilationZones, removeRefreshListener]);

  const extractZonesForDisplay = useCallback((zoneSet: VentilationZonesResponseType) => {
    const zoneCount =
      zoneSet.zone_count ||
      Object.entries(zoneSet).filter(([key, value]) => key.startsWith("zone_") && value !== null)
        .length;
    return Array.from(Array(zoneCount).keys()).map((key) => {
      const prevKey = `zone_${key}` as keyof VentilationZonesResponseType;
      const currKey = `zone_${key + 1}` as keyof VentilationZonesResponseType;
      return {
        // DEX-788: ventilation VE floor is 5 L/min (zone 1 starts at the min, not 0);
        // ascending validation then keeps every entered value above the min.
        bottom: key ? Number(zoneSet[prevKey]) + 1 : 5,
        top: key === zoneCount - 1 ? ("max" as Zone["top"]) : Number(zoneSet[currKey]) || 0,
        name: t`Zone ${key + 1}`,
      };
    });
  }, []);

  const extractZonesForSave = useCallback((zoneSet: ZoneSet) => {
    const {
      zones,
      zone_1: _zone_1,
      zone_2: _zone_2,
      zone_3: _zone_3,
      zone_4: _zone_4,
      zone_5: _zone_5,
      zone_6: _zone_6,
      zone_7: _zone_7,
      zone_8: _zone_8,
      zone_9: _zone_9,
      zone_10: _zone_10,
      ...rest
    } = zoneSet;
    const update: Omit<ZoneSet, "zones"> = rest || {};
    zones.forEach((zone, index) => {
      if (index > 0) {
        (update[`zone_${index}` as keyof Omit<ZoneSet, "zones">] as number) = zone.bottom - 1;
      }
    });
    update.zone_count = zones.length;
    return update;
  }, []);

  const savedZoneSets: Record<number, ZoneSet> = useMemo(() => {
    const zoneSetMap = {} as Record<number, ZoneSet>;
    if (!ventilationZoneData) {
      return zoneSetMap;
    }
    for (const zoneSetIndex in ventilationZoneData) {
      const zoneSet = ventilationZoneData[zoneSetIndex];
      const zones = extractZonesForDisplay(zoneSet);
      zoneSetMap[zoneSet.id] = {
        ...zoneSet,
        zones,
        // This assigns 0 to the primary zone set, so it always displays first,
        // The indices for the non-primary zone sets are all incremented by 1 so
        // they won't be 0, but their order is maintained
        displayIndex: zoneSet.primary ? 0 : +zoneSetIndex + 1,
        name: zoneSet.name || t`Set ${+zoneSetIndex + 1}`,
        zone_count: zones.length,
      };
    }
    return zoneSetMap;
  }, [ventilationZoneData, extractZonesForDisplay]);

  const updatedAt = useMemo(() => {
    const updatedAtMap = {} as Record<number, Date | undefined>;
    if (!ventilationZoneData) {
      return updatedAtMap;
    }
    for (const zoneSet of ventilationZoneData) {
      updatedAtMap[zoneSet.id] = (zoneSet.updated_at && new Date(zoneSet.updated_at)) || undefined;
    }
    return updatedAtMap;
  }, [ventilationZoneData]);

  const updateZoneSet = useCallback(
    async (zoneSet: ZoneSet) => {
      const update = extractZonesForSave(zoneSet);
      if (wahooToken && zoneSet.id) {
        const newZoneSet = await ventilationZonesClient.put(wahooToken, update, zoneSet.id);
        mutateVentilationZones(
          ventilationZoneData?.map((set) => {
            if (set.id === zoneSet.id) {
              return { ...set, ...update };
            }
            return set;
          }),

          { revalidate: false }
        );
        return newZoneSet;
      }
    },
    [
      extractZonesForSave,
      ventilationZoneData,
      ventilationZonesClient,
      mutateVentilationZones,
      wahooToken,
    ]
  );

  const createZoneSet = useCallback(
    async (zoneSet: ZoneSet) => {
      const update = extractZonesForSave(zoneSet);
      // Unlike HR/power/pace, ventilation has no cloud-provided default set. The assignment
      // UI needs a primary set to act as the default bucket for unassigned profiles, so when
      // no primary exists yet make this newly created set the primary.
      if (!ventilationZoneData?.some((set) => set.primary)) {
        update.primary = true;
      }
      if (wahooToken) {
        const newZoneSet = await ventilationZonesClient.postForCurrentUser(wahooToken, update);
        const newZoneSets = [...(ventilationZoneData || []), newZoneSet];
        mutateVentilationZones(newZoneSets, { revalidate: false });
        mutateUser();
        return newZoneSet;
      }
    },
    [
      extractZonesForSave,
      ventilationZoneData,
      ventilationZonesClient,
      mutateVentilationZones,
      mutateUser,
      wahooToken,
    ]
  );

  const saveZoneSet = useCallback(
    async (zoneSet: ZoneSet) => {
      if (zoneSet.id === -1) {
        return createZoneSet(zoneSet);
      } else {
        return updateZoneSet(zoneSet);
      }
    },
    [createZoneSet, updateZoneSet]
  );

  // Ventilation zones have no server-side algorithm lookup.
  const autoCalcZoneSet = useCallback(async () => undefined, []);

  const clearAlgorithmInfo = useCallback((zoneSets: Record<number, ZoneSet>) => zoneSets, []);

  const maxZoneValue = 300; // DEX-788: max VE (L/min); zone floor min is 5 (see extractZonesForDisplay)

  const zoneCountChoices = useMemo(
    () => getSettingsRemoteConfigObject<number[]>("VENTILATION_ZONE_COUNT_CHOICES") ?? [5],
    [getSettingsRemoteConfigObject]
  );

  const autoCalculateVariant = "ventilation";

  const profileField = "ventilation_zone_id";

  const defaultZones: Zone[] = useMemo(
    () =>
      DEFAULT_VENTILATION_ZONE_BOTTOMS.map((bottom, index, bottoms) => ({
        bottom,
        top: index === bottoms.length - 1 ? ("max" as Zone["top"]) : bottoms[index + 1] - 1,
        name: t`Zone ${index + 1}`,
      })),
    []
  );

  const primaryZoneSetId = useMemo(
    () => ventilationZoneData?.find((zone) => zone.primary)?.id,
    [ventilationZoneData]
  );

  const deleteZoneSet = useCallback(
    async (zoneSetId: number) => {
      await Promise.all(
        workoutProfiles
          .filter((profile) => profile.ventilation_zone_id === zoneSetId)
          .map(
            async (profile) =>
              await updateProfile(profile.external_id, "ventilation_zone_id", primaryZoneSetId)
          )
      );
      await ventilationZonesClient.delete(wahooToken, zoneSetId);
      if (ventilationZoneData) {
        mutateVentilationZones(ventilationZoneData.filter((zoneSet) => zoneSet.id !== zoneSetId));
        mutateUser();
      }
      mutateWorkoutProfiles();
    },
    [
      ventilationZoneData,
      ventilationZonesClient,
      mutateVentilationZones,
      mutateUser,
      mutateWorkoutProfiles,
      primaryZoneSetId,
      updateProfile,
      wahooToken,
      workoutProfiles,
    ]
  );

  const reloadZones = useCallback(() => mutateVentilationZones(), [mutateVentilationZones]);

  const maxAutoCalculateValues = useMemo(() => ({}), []);

  const createValidZones = useCallback(
    (zoneSetId: number, startingIndex: number, zoneSets: Record<number, ZoneSet>) => {
      const correctedZones = copyZones(zoneSets);
      const zoneSet = correctedZones[zoneSetId].zones;
      zoneSet[startingIndex].bottom = Math.round(zoneSet[startingIndex].bottom);
      zoneSet[startingIndex - 1].top = zoneSet[startingIndex].bottom - 1;

      // ensure zones are in ascending order. Assumes only the zone at startingIndex
      // has been changed, and the zones were in a valid state before the change
      for (let i = startingIndex; i < zoneSet.length; i++) {
        if (zoneSet[i].bottom <= zoneSet[i - 1].bottom) {
          zoneSet[i].bottom = +zoneSet[i - 1].bottom + 1;
          zoneSet[i - 1].top = +zoneSet[i - 1].bottom;
          // if the value at the starting index was just corrected, there is no need to continue.
          // The values above the starting index can't be less than the value at the starting index if the starting index value was lowered.
          if (i === startingIndex) {
            break;
          }
        }
        // if the value at startingIndex was already above the value below it, it may have been raised, so the values above it need to be checked
        else if (i === startingIndex) {
          continue;
        }
        // if i is an index above startingIndex, and the value was already above the value below it, then everything should already be ascending
        else {
          break;
        }
      }

      //ensure corrections didn't put zones above the max value
      for (let i = zoneSet.length - 1; i > 0; i--) {
        const topValue = zoneSet[i].top === "max" ? maxZoneValue : zoneSet[i].top;
        if (typeof topValue === "number" && zoneSet[i].bottom > topValue) {
          zoneSet[i].bottom = +topValue;
          zoneSet[i - 1].top = +topValue - 1;
        } else {
          break;
        }
      }
      return correctedZones;
    },
    [maxZoneValue]
  );

  const zonesSetIsValid: (zoneSet: ZoneSet, zoneCount: number) => boolean = useCallback(
    (zoneSet: ZoneSet, zoneCount: number) => {
      return (
        zoneSet.zones.every(
          (zone, index, array) => index === 0 || zone.bottom > array[index - 1].bottom
        ) && zoneCountChoices?.includes(zoneCount)
      );
    },
    [zoneCountChoices]
  );

  return {
    savedZoneSets,
    updatedAt,
    saveZoneSet,
    maxZoneValue,
    zoneCountChoices,
    autoCalculateVariant,
    profileField,
    autoCalcZoneSet,
    primaryZoneSetId,
    deleteZoneSet,
    clearAlgorithmInfo,
    error,
    isLoading,
    reloadZones,
    maxAutoCalculateValues,
    zonesSetIsValid,
    createValidZones,
    extractZonesForDisplay,
    defaultZones,
  };
};

export default useVentilationZones;
